import React, { useContext, useState } from 'react';
import {
    Select,
    MenuItem,
    FormControl,
    Typography,
    Backdrop,
    CircularProgress,
    Box,
} from '@mui/material';
import { Business } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { AppContext } from '../../AppContext';

const useStyles = makeStyles(() => ({
    companyContainer: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: '1rem',
        marginRight: '2rem',
    },
    companyIcon: {
        color: '#e14954',
        marginRight: '8px',
        fontSize: '20px',
    },
    companyName: {
        fontStyle: 'normal',
        fontWeight: '700',
        fontSize: '14px',
        lineHeight: '17px',
        textTransform: 'uppercase',
        color: '#271D1C',
        whiteSpace: 'nowrap',
    },
    companySelect: {
        minWidth: '200px',
        '& .MuiSelect-select': {
            fontStyle: 'normal',
            fontWeight: '700',
            fontSize: '14px',
            lineHeight: '17px',
            textTransform: 'uppercase',
            color: '#271D1C',
            padding: '8px 12px',
        },
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#f5f6fa',
            '& fieldset': {
                borderColor: '#e5e9f2',
            },
            '&:hover fieldset': {
                borderColor: '#e14954',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#e14954',
            },
        },
    },
    loadingBackdrop: {
        zIndex: 9999,
        color: '#e14954',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    loadingText: {
        marginTop: '16px',
        color: '#271D1C',
        fontWeight: '600',
    },
}));

function CompanyPicker() {
    const classes = useStyles();
    const { company, companies, switchCompany } = useContext(AppContext);
    const [isLoading, setIsLoading] = useState(false);

    const handleCompanyChange = async (event) => {
        const selectedCompanyName = event.target.value;
        if (selectedCompanyName === company?.name) {
            return; // No change needed
        }

        setIsLoading(true);

        try {
            await switchCompany(selectedCompanyName);

            // A full reload resets the cart/table state and loads the selected
            // company's authorized POS Profiles before accepting another order.
            window.location.reload();
        } catch (error) {
            console.error('Error changing company:', error);
            setIsLoading(false);
        }
    };

    // Don't render if no companies or no current company
    if (!companies || !company) {
        return null;
    }

    return (
        <>
            <div className={classes.companyContainer}>
                <Business className={classes.companyIcon} />
                {companies.length === 1 ? (
                    // Single company - just display the name
                    <Typography className={classes.companyName}>
                        {company.company_name || company.name}
                    </Typography>
                ) : (
                    // Multiple companies - show selector
                    <FormControl variant="outlined" size="small" className={classes.companySelect}>
                        <Select
                            value={company.name || ''}
                            onChange={handleCompanyChange}
                            displayEmpty
                            disabled={isLoading}
                        >
                            {companies.map((comp) => (
                                <MenuItem key={comp.name} value={comp.name}>
                                    <Box display="flex" alignItems="center">
                                        <Typography variant="body2">
                                            {comp.company_name || comp.name}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </div>

            {/* Loading backdrop */}
            <Backdrop className={classes.loadingBackdrop} open={isLoading}>
                <div className={classes.loadingContainer}>
                    <CircularProgress size={60} />
                    <Typography variant="h6" className={classes.loadingText}>
                        Switching Company...
                    </Typography>
                </div>
            </Backdrop>
        </>
    );
}

export default CompanyPicker;
